import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import type { JSX } from 'react'
import { Typography, Chip, Box } from '@mui/material'
import React from 'react'
import { useCommonColumnsKyb, useCommonColumnsKyc } from './CommonColumn'
import { useBrColumnsKyb, useBrColumnsKyc } from './BrColumns'
import { useSelector } from 'react-redux'

export const ShowMoreListCell = ({ items }: { items: string[] }) => {
  const { t } = useTranslation(['common'])
  const [showMore, setShowMore] = React.useState(false)
  const displayItems = showMore ? items : items.slice(0, 3)
  const toggleShowMore = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowMore((prev) => !prev)
  }
  if (!items || items.length === 0) return <Typography variant="body2">---</Typography>
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, alignItems: 'flex-start', width: '100%' }}>
        {displayItems.map((item, idx) => (
          <Typography key={idx} variant="body2" sx={{ my: 0.1, mx: 0.5 }}>{item}</Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1, mb: 1, minHeight: 32 }}>
        {items.length > 3 ? (
          <Chip
            label={showMore ? t('common:showLess') : t('common:showMore')}
            onClick={toggleShowMore}
            size="small"
            sx={{
              backgroundColor: '#19DB55',
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#19DB55',
              },
            }}
          />
        ) : (
          <Box sx={{ height: 32 }} />
        )}
      </Box>
    </Box>
  )
}

export const ShowMoreCell = ({ text }: { text: string }) => {
  const [showMore, setShowMore] = useState(false)
  const { t } = useTranslation(['common'])
  const toggleShowMore = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowMore((prev) => !prev)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        mt: 1,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxHeight: showMore ? '220px' : 'none',
          overflowY: showMore ? 'auto' : 'visible',
          overflowX: 'auto',
          transition: 'max-height 0.2s',
          pr: 1,
          boxSizing: 'border-box',
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', width: '100%', pb: 1 }}>
          {showMore ? text : `${text?.slice(0, 100)}${text && text.length > 100 ? '...' : ''}`}
        </Typography>
      </Box>
      {text && text.length > 100 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', mt: 1, pb: 1 }}>
          <Chip
            label={showMore ? t('common:showLess') : t('common:showMore')}
            onClick={toggleShowMore}
            size="small"
            sx={{
              backgroundColor: '#19DB55',
              color: '#fff',
              '&:hover': {
          backgroundColor: '#19DB55',
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export type TColumn = {
  field: string
  headerName: string
  minWidth: number
  maxWidth?: number
  headerAlign: string
  flex?: number
  // eslint-disable-next-line no-unused-vars
  renderCell: ({ row }: { row: any }) => JSX.Element
  sortable?: boolean
}

const CRColumnsKyb: TColumn[] = []
const ARColumnsKyb: TColumn[] = []
const COColumnsKyb: TColumn[] = []

export const useDataTableColumnsKyb = () => {
  const { currentCountry } = useSelector((state: any) => state.kybCountry)
  const { t } = useTranslation(['common', 'messages'])
  const commonColumnsKyb = useCommonColumnsKyb(t)
  const brColumnsKyb = useBrColumnsKyb(t)

  return useMemo<TColumn[]>(() => {
    switch (currentCountry) {
      case 'BR':
        return [...commonColumnsKyb, ...brColumnsKyb]
      case 'CO':
        return [...commonColumnsKyb, ...COColumnsKyb]
      case 'AR':
        return [...commonColumnsKyb, ...ARColumnsKyb]
      case 'CR':
        return [...commonColumnsKyb, ...CRColumnsKyb]
      default:
        return []
    }
  }, [currentCountry, commonColumnsKyb, brColumnsKyb])
}

const CRColumnsKyc: TColumn[] = []
const ARColumnsKyc: TColumn[] = []
const COColumnsKyc: TColumn[] = []

export const useDataTableColumnsKyc = (currentCountry: string) => {
  const { t } = useTranslation(['common', 'messages'])
  const commonColumnsKyc = useCommonColumnsKyc(t, currentCountry)
  const brColumnsKyc = useBrColumnsKyc(t)

  return useMemo<TColumn[]>(() => {
    switch (currentCountry) {
      case 'BR':
        return [...commonColumnsKyc, ...brColumnsKyc]
      case 'CO':
        return [...commonColumnsKyc, ...COColumnsKyc]
      case 'AR':
        return [...commonColumnsKyc, ...ARColumnsKyc]
      case 'CR':
        return [...commonColumnsKyc, ...CRColumnsKyc]
      default:
        return commonColumnsKyc
    }
  }, [currentCountry, commonColumnsKyc, brColumnsKyc])
}
